import { Box, BoxProps, Divider, Flex, Text } from '@chakra-ui/react'
import { usePopUpMessage } from '@hb/react'
import { DropdownField, FieldTypes, Medication, Supplement, WithId } from '@hb/shared'
import {
  getMedicationField,
  getSupplementField,
} from '@hb/shared/src/fields/gyn/medicationsAndSupplements'
import { FORM_ERROR } from 'final-form'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ActionButton, SolidActionButton } from '../Buttons'
import { SimpleForm, StandaloneInput } from '../forms'
import { DefaultModal } from '../Modals'
import { useGynCareView } from './context'

// const field: ListField = {
//   name: 'Medications',
//   optional: true,
//   itemFields: getMedicationField(false),
//   itemName: 'Medication',
// }

// const fieldMap = getMedicationField(false)

const formBoxProps: BoxProps = {
  bg: 'gray.50',
}

export const MedicationOrSupplementModal = () => {
  const { conditionMedicationModal, patient } = useGynCareView()
  const { showError } = usePopUpMessage()
  const {
    openMedication: openMedication,
    closeMedicationConditionModal,
    selectedMedication,
    handleSubmitMedication,
    medicationsArray,
    supplementsArray,
    selectedCondition,
  } = conditionMedicationModal

  const field = useMemo(
    () =>
      openMedication?.type === 'medication'
        ? getMedicationField(!openMedication?.conditionPath)
        : getSupplementField(!openMedication?.conditionPath),
    [openMedication],
  )

  const [existingMedication, setExistingMedication] = useState<
    WithId<Medication> | WithId<Supplement> | null
  >(null)
  const [mode, setMode] = useState<'add-existing' | 'add-new'>('add-existing')
  useEffect(() => {
    if (
      !selectedMedication &&
      openMedication?.conditionPath &&
      (!openMedication || medicationsArray.length)
    ) {
      setExistingMedication(null)
      setMode('add-existing')
      return
    }
    setMode('add-new')
  }, [medicationsArray, openMedication, selectedMedication])

  const existingMedicationsField = useMemo<DropdownField>(
    () =>
      openMedication?.type === 'medication'
        ? {
            type: FieldTypes.DROPDOWN,
            placeholder: 'Add Existing medication',
            optional: true,
            options: medicationsArray.map(medication => ({
              text: medication.name,
              id: medication.id,
            })),
          }
        : {
            type: FieldTypes.DROPDOWN,
            placeholder: 'Add Existing supplement',
            optional: true,
            options: supplementsArray.map(supplement => ({
              text: supplement.name,
              id: supplement.id,
            })),
          },
    [medicationsArray, supplementsArray, openMedication],
  )

  const handleSelectExistingMedication = useCallback(
    (id: string) => {
      const medication =
        openMedication?.type === 'medication'
          ? patient.gyn?.medications?.[id]
          : patient.gyn?.supplements?.[id]
      if (!medication) return
      setExistingMedication({ id, ...medication })
    },
    [patient.gyn, openMedication],
  )

  const [submittingExistingMedication, setSubmittingExistingMedication] = useState(false)
  const handleSubmitExistingMedication = useCallback(async () => {
    if (!existingMedication) return
    setSubmittingExistingMedication(true)
    try {
      await handleSubmitMedication(existingMedication, existingMedication.id)
      closeMedicationConditionModal()
    } catch (error: any) {
      const message = error.message ?? 'An error occurred'
      showError(message)
    }
    setSubmittingExistingMedication(false)
  }, [existingMedication, handleSubmitMedication, closeMedicationConditionModal, showError])

  const title = useMemo(() => {
    if (selectedCondition)
      return `${openMedication?.type === 'medication' ? 'Medications' : 'Supplements'} for ${selectedCondition}`
    return selectedMedication
      ? selectedMedication.name
      : `Add New ${openMedication?.type === 'medication' ? 'Medication' : 'Supplement'}`
  }, [selectedCondition, selectedMedication, openMedication])

  let body: React.ReactNode = null
  if (mode === 'add-existing') {
    body = (
      <Flex flexFlow="column" gap={2} p={3} w="100%">
        <Flex alignItems="center" w="100%" gap={2}>
          <Box flex={1} minW="0">
            <StandaloneInput
              field={existingMedicationsField}
              value={existingMedication?.id}
              theme="detailed"
              onChange={value => handleSelectExistingMedication(value)}
            />
          </Box>
        </Flex>
        {existingMedication ? (
          <SolidActionButton
            isLoading={submittingExistingMedication}
            onClick={handleSubmitExistingMedication}
            colorScheme="green"
            py={3}
            height="auto"
            whiteSpace="normal"
            w="100%">
            Add {existingMedication.name} for {selectedCondition}
          </SolidActionButton>
        ) : null}
        <Divider />
        <ActionButton onClick={() => setMode('add-new')}>
          Add New {openMedication?.type === 'medication' ? 'Medication' : 'Supplement'}
        </ActionButton>
      </Flex>
    )
  } else {
    body = (
      <SimpleForm
        value={selectedMedication}
        field={field}
        boxProps={formBoxProps}
        onSubmit={async data => {
          try {
            await handleSubmitMedication(data, openMedication?.medicationId)
            closeMedicationConditionModal()
            return undefined
          } catch (error: any) {
            const message = error.message ?? 'An error occurred'
            showError(message)
            return { [FORM_ERROR]: message }
          }
        }}
      />
    )
  }

  return (
    <DefaultModal
      isOpen={!!openMedication}
      onClose={closeMedicationConditionModal}
      size="2xl"
      overlayHeader
      render={() => (
        <Flex flexFlow="column" w="100%">
          <Flex borderBottom="1px solid #cdcdcd" w="100%" px={4} py={2}>
            <Text pr={10} fontFamily="Open Sans" fontWeight={500} fontSize="lg">
              {title}
            </Text>
          </Flex>
          <Box w="100%" bg="gray.50">
            {selectedMedication?.conditions ? (
              <Box px={4} pt={2} w="100%">
                <Box bg="white" p={2} border="1px solid #cdcdcd" borderRadius={3} w="100%">
                  <Text fontWeight={500} color="gray.600" fontFamily="Open Sans" fontSize="lg">
                    Used to treat:
                  </Text>
                  <Flex flexFlow="row wrap" gap={2}>
                    {selectedMedication.conditions.map(condition => (
                      <Text
                        bg="gray.50"
                        border="1px solid #cdcdcd"
                        borderRadius={3}
                        py={1}
                        px={2}
                        key={condition.path}>
                        {condition.name}
                      </Text>
                    ))}
                  </Flex>
                </Box>
              </Box>
            ) : null}
            {body}
          </Box>
        </Flex>
      )}
    />
  )
}
